import { createMachine, assign, enqueueActions, stateIn } from 'xstate';
import { getLocationIdsFromProvidersData } from '../../map-search/lib/utils';
import * as states from './map-search-states';
import * as actions from './map-search-actions';
import {
  MAP_SNACKBAR_EXPIRY_KEY,
  MAP_SNACKBAR_TTL
} from '../../map-search/lib/constants';
import { PatientRelationship } from '@kyruus/provider-components';
import { fromServices, serviceInput } from '../utils';

// outline for the context of the machine. facilitates proper
// initialization/error handling on startup
const emptyContext = {
  deepLinkingParams: undefined,
  searchParams: undefined,
  providers: undefined,
  locations: undefined,
  selectedLocation: undefined,
  providersForDrawer: undefined,
  numPagesToLoadInDrawer: undefined,
  hasMoreProvidersToLoad: undefined,
  fetchProviders: () => {},
  fetchLocationsWithIds: () => {},
  fetchProvidersByLocation: () => {},
  fetchSlots: () => Promise.resolve(),
  config: {}
};

function getApptInfoFromSearchParams(searchParams) {
  const filters = Array.isArray(searchParams?.filter)
    ? searchParams.filter
    : searchParams?.filter
      ? [searchParams.filter]
      : [];

  const splitOnFirst = (str, sep) => {
    const idx = str.indexOf(sep);
    return idx === -1 ? [str] : [str.slice(0, idx), str.slice(idx + 1)];
  };

  const relationshipParam = filters.find((value) =>
    /^provider\.appointment_ehr_purposes\.patient_relationship/.test(value)
  );
  const purposeParam = filters.find((value) =>
    /^provider\.appointment_ehr_purposes\.name/.test(value)
  );
  const visitMethodParam = filters.find((value) =>
    /^provider\.appointment_ehr_purposes\.visit_method/.test(value)
  );

  return {
    relationship: relationshipParam
      ? splitOnFirst(relationshipParam, ':')[1]
      : undefined,
    purpose: purposeParam ? splitOnFirst(purposeParam, ':')[1] : undefined,
    visitMethod: visitMethodParam
      ? splitOnFirst(visitMethodParam, ':')[1]
      : undefined
  };
}

// v5 resolves conflicting transitions in parallel regions like SCXML: the map
// region's CLICK_PIN / OPEN_SORT_AND_FILTER exit IDLE, so the snackbar region's
// transitions for the same events never run. Save the expiry from here instead.
const expireSnackbarIfShown = enqueueActions(({ check, enqueue }) => {
  if (check(stateIn({ [states.IDLE]: { [states.SNACKBAR]: states.SHOW } }))) {
    enqueue(actions.SET_SNACKBAR_EXPIRATION);
  }
});

/**
 * Returns the machine JSON for map-search. Exported to facilitate testing
 * @param {object} initialContext the initial context for the machine
 * @returns object
 */
export const machineJSON = (initialContext = emptyContext) => {
  const {
    deepLinkingParams,
    searchParams,
    providers,
    locations,
    selectedLocation,
    providersForDrawer,
    fetchProviders,
    fetchLocationsWithIds,
    fetchProvidersByLocation,
    fetchSlots,
    config
  } = initialContext;
  return {
    id: 'map-search',
    context: {
      deepLinkingParams: deepLinkingParams,
      searchParams: searchParams,
      providers: providers,
      locations: locations,
      selectedLocation: selectedLocation,
      providersForDrawer: providersForDrawer,
      numPagesToLoadInDrawer: 1,
      hasMoreProvidersToLoad: false,
      fetchProviders: fetchProviders,
      fetchLocationsWithIds: fetchLocationsWithIds,
      fetchProvidersByLocation: fetchProvidersByLocation,
      fetchSlots: fetchSlots,
      config: config
    },
    initial: states.INITIALIZING,

    states: {
      [states.INITIALIZING]: {
        initial: states.ROUTING,
        states: {
          [states.ROUTING]: {
            always: [
              {
                guard: 'hasProvidersAndLocationsInContext',
                target: states.DONE
              },
              {
                guard: 'hasSearchParamsAndProvidersInContext',
                target: states.FETCHING_LOCATIONS
              },
              {
                guard: 'hasSearchParamsInContext',
                target: states.FETCHING_PROVIDERS
              },
              {
                target: states.ERROR
              }
            ]
          },
          [states.FETCHING_PROVIDERS]: {
            invoke: {
              id: 'fetchProviders',
              src: 'fetchProviders',
              input: serviceInput,
              onDone: [
                {
                  guard: 'hasProviderResults',
                  actions: actions.SET_PROVIDERS,
                  target: states.FETCHING_LOCATIONS
                },
                {
                  target: `#${states.NO_RESULTS}`
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.FETCHING_LOCATIONS]: {
            invoke: {
              id: 'fetchLocations',
              src: 'fetchLocations',
              input: serviceInput,
              onDone: [
                {
                  guard: 'hasLocationResults',
                  actions: actions.SET_LOCATIONS,
                  target: states.DONE
                },
                {
                  target: `#${states.NO_RESULTS}`
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.DONE]: { type: 'final' },
          [states.NO_RESULTS]: { type: 'final' },
          [states.ERROR]: { type: 'final' }
        },
        onDone: [
          {
            guard: 'hasProvidersAndLocationsInContext',
            target: states.CHECKING_DEEPLINK
          },
          {
            target: states.ERROR
          }
        ]
      },
      [states.CHECKING_DEEPLINK]: {
        always: [
          {
            // if we have deeplink params, save them and route to open provider drawer
            guard: 'hasValidDeepLinkingParams',
            actions: actions.SET_CONTEXT_FROM_DEEPLINK,
            target: states.PROVIDER_DRAWER_OPEN
          },
          {
            // otherwise go to default idle state
            target: states.IDLE
          }
        ]
      },
      [states.IDLE]: {
        type: 'parallel',
        states: {
          [states.IDLE_MAP_SEARCH]: {
            on: {
              [actions.BACK_TO_LIST_VIEW]: {},
              [actions.CLICK_PIN]: {
                actions: [actions.SET_SELECTED_LOCATION, expireSnackbarIfShown],
                target: `#${states.PROVIDER_DRAWER_OPEN}`
              },
              [actions.OPEN_SORT_AND_FILTER]: {
                actions: expireSnackbarIfShown,
                target: `#${states.SORT_AND_FILTER_DRAWER_OPEN}`
              },
              [actions.UPDATE_SEARCH_PARAMS]: {
                actions: actions.UPDATE_SEARCH_PARAMS,
                target: `#${states.FETCHING_PROVIDERS_AND_LOCATIONS}`
              }
            }
          },
          [states.SNACKBAR]: {
            initial: states.INITIALIZING,
            states: {
              [states.INITIALIZING]: {
                always: [
                  { target: states.SHOW, guard: 'shouldShowSnackbar' },
                  { target: states.NO_SHOW }
                ]
              },
              [states.SHOW]: {
                on: {
                  [actions.BACK_TO_LIST_VIEW]: {},
                  [actions.CLOSE_SNACKBAR]: {
                    actions: actions.SET_SNACKBAR_EXPIRATION,
                    target: states.NO_SHOW
                  }
                }
              },
              [states.NO_SHOW]: {}
            }
          }
        }
      },
      [states.PROVIDER_DRAWER_OPEN]: {
        id: states.PROVIDER_DRAWER_OPEN,
        initial: states.FETCHING_PROVIDERS,
        onDone: { target: states.IDLE },
        states: {
          [states.FETCHING_PROVIDERS]: {
            // reset page number anytime we fetch a new set of providers
            entry: actions.RESET_PROVIDER_PAGE_NUM,
            invoke: {
              id: 'fetchProvidersByLocationId',
              src: 'fetchProvidersByLocationId',
              input: serviceInput,
              onDone: [
                {
                  guard: 'isSingleBookableLocation',
                  actions: actions.SET_PROVIDERS_FOR_DRAWER,
                  target: states.RENDER_SINGLE_LOCATION
                },
                {
                  guard: 'hasProviderResults',
                  actions: [
                    actions.SET_PROVIDERS_FOR_DRAWER,
                    actions.SET_HAS_MORE_PROVIDERS_TO_LOAD
                  ],
                  target: states.FETCHING_SLOTS
                },
                {
                  target: states.NO_RESULTS
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.FETCHING_SLOTS]: {
            invoke: {
              src: 'fetchSlotsForDrawer',
              input: serviceInput,
              onDone: { target: states.RENDER_PROVIDERS },
              onError: { target: states.RENDER_PROVIDERS }
            }
          },
          [states.RENDER_PROVIDERS]: {
            on: {
              [actions.BACK_TO_LIST_VIEW]: {},
              [actions.CLOSE_PROVIDER_DRAWER]: {
                actions: actions.UNSET_SELECTED_LOCATION,
                target: states.CLOSED
              },
              [actions.CLICK_PIN]: {
                actions: actions.SET_SELECTED_LOCATION,
                target: states.FETCHING_PROVIDERS
              },
              [actions.UPDATE_SEARCH_PARAMS]: {
                actions: actions.UPDATE_SEARCH_PARAMS,
                target: states.FETCHING_PROVIDERS
              },
              [actions.TOGGLE_GLOBAL_AVAILABILITY_MODAL]: {
                // TODO do we need this? whole machine here... or maybe just more substates
              },
              [actions.TOGGLE_PROVIDER_AVAILABILITY_MODAL]: {
                // TODO do we need this? whole machine here... or maybe just more substates
              },
              [actions.OPEN_BOOKING_CTA_MODAL]: {
                // TODO do we need this? booking CTA is part of tile, not machine
                target: states.BOOKING_CTA_MODAL_OPEN
              },
              [actions.LOAD_MORE_PROVIDERS]: {
                actions: actions.INCREMENT_PROVIDER_PAGE_NUM,
                target: states.LOADING_MORE_PROVIDERS
              }
            }
          },
          [states.RENDER_SINGLE_LOCATION]: {
            on: {
              [actions.BACK_TO_LIST_VIEW]: {},
              [actions.CLICK_PIN]: {
                actions: actions.SET_SELECTED_LOCATION,
                target: states.FETCHING_PROVIDERS
              },
              [actions.CLOSE_PROVIDER_DRAWER]: {
                actions: actions.UNSET_SELECTED_LOCATION,
                target: states.CLOSED
              }
            }
          },
          [states.LOADING_MORE_PROVIDERS]: {
            invoke: {
              id: 'fetchProvidersByLocationId',
              src: 'fetchProvidersByLocationId',
              input: serviceInput,
              onDone: [
                {
                  guard: 'hasProviderResults',
                  actions: [
                    actions.LOAD_MORE_PROVIDERS_INTO_DRAWER,
                    actions.SET_HAS_MORE_PROVIDERS_TO_LOAD
                  ],
                  target: states.RENDER_PROVIDERS
                },
                {
                  target: states.NO_RESULTS
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.BOOKING_CTA_MODAL_OPEN]: {
            on: {
              [actions.CLOSE_BOOKING_CTA_MODAL]: {
                target: states.RENDER_PROVIDERS
              },
              [actions.CLICK_BOOK_BUTTON]: {}
            }
          },
          [states.NO_RESULTS]: {
            on: {
              [actions.CLOSE_PROVIDER_DRAWER]: {
                target: states.CLOSED
              }
            }
          },
          [states.ERROR]: {
            on: {
              [actions.CLOSE_PROVIDER_DRAWER]: {
                target: states.CLOSED
              }
            }
          },
          [states.CLOSED]: { type: 'final' }
        }
      },
      [states.SORT_AND_FILTER_DRAWER_OPEN]: {
        id: states.SORT_AND_FILTER_DRAWER_OPEN,
        initial: states.IDLE,
        onDone: { target: states.IDLE },
        states: {
          [states.IDLE]: {
            on: {
              [actions.CLOSE_SORT_AND_FILTER]: {
                target: states.CLOSED
              },
              [actions.CLEAR_SEARCH_FILTERS]: {
                actions: actions.CLEAR_SEARCH_FILTERS,
                target: states.FETCHING_PROVIDERS_AND_LOCATIONS
              },
              [actions.SELECT_SORT_OPTION]: {
                actions: actions.SELECT_SORT_OPTION,
                target: states.FETCHING_PROVIDERS_AND_LOCATIONS
              },
              [actions.UPDATE_SEARCH_PARAMS]: {
                actions: actions.UPDATE_SEARCH_PARAMS,
                target: states.FETCHING_PROVIDERS_AND_LOCATIONS
              },
              [actions.CLICK_PIN]: {
                actions: actions.SET_SELECTED_LOCATION,
                target: `#${states.PROVIDER_DRAWER_OPEN}`
              }
            }
          },
          [states.FETCHING_PROVIDERS_AND_LOCATIONS]: {
            invoke: {
              id: 'fetchProvidersAndLocations',
              src: 'fetchProvidersAndLocations',
              input: serviceInput,
              onDone: [
                {
                  guard: 'hasProviderAndLocationResults',
                  actions: actions.SET_PROVIDERS_AND_LOCATIONS,
                  target: states.IDLE
                },
                {
                  guard: 'hasProviderNoMlocData',
                  target: states.NO_MLOC_DATA
                },
                {
                  target: states.NO_RESULTS
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.NO_RESULTS]: {},
          [states.NO_MLOC_DATA]: {},
          [states.ERROR]: {},
          [states.CLOSED]: { type: 'final' }
        }
      },
      [states.FETCHING_PROVIDERS_AND_LOCATIONS]: {
        id: states.FETCHING_PROVIDERS_AND_LOCATIONS,
        invoke: {
          id: 'fetchProvidersAndLocations',
          src: 'fetchProvidersAndLocations',
          input: serviceInput,
          onDone: [
            {
              guard: 'hasProviderAndLocationResults',
              actions: actions.SET_PROVIDERS_AND_LOCATIONS,
              target: states.IDLE
            },
            {
              target: states.NO_RESULTS
            }
          ],
          onError: {
            target: states.ERROR
          }
        }
      },
      [states.NO_RESULTS]: {
        id: states.NO_RESULTS,
        on: {
          [actions.SEARCH_THIS_AREA]: {
            actions: actions.UPDATE_SEARCH_CENTER_AND_RADIUS,
            target: states.FETCHING_PROVIDERS_AND_LOCATIONS
          }
        }
      },
      [states.ERROR]: {}
    }
  };
};

const machineActions = {
  [actions.SET_PROVIDERS]: assign({
    providers: ({ event }) => event.output
  }),
  [actions.SET_LOCATIONS]: assign({
    locations: ({ event }) => event.output
  }),
  [actions.SET_PROVIDERS_AND_LOCATIONS]: assign({
    locations: ({ event }) => event.output.locations,
    providers: ({ event }) => event.output.providers
  }),
  [actions.SET_CONTEXT_FROM_DEEPLINK]: assign({
    selectedLocation: ({ context }) =>
      context.locations._locations.find(
        (loc) => loc.id === context.deepLinkingParams.locationId.toUpperCase()
      )
  }),
  [actions.SET_SELECTED_LOCATION]: assign({
    selectedLocation: ({ event }) => event.data
  }),
  [actions.UNSET_SELECTED_LOCATION]: assign({
    selectedLocation: () => undefined
  }),
  [actions.SET_PROVIDERS_FOR_DRAWER]: assign({
    providersForDrawer: ({ event }) => event.output
  }),
  [actions.LOAD_MORE_PROVIDERS_INTO_DRAWER]: assign({
    providersForDrawer: ({ context, event }) => {
      return {
        ...context.providersForDrawer,
        _result: [
          ...context.providersForDrawer._result,
          ...event.output._result
        ]
      };
    }
  }),
  [actions.INCREMENT_PROVIDER_PAGE_NUM]: assign({
    numPagesToLoadInDrawer: ({ context }) => context.numPagesToLoadInDrawer + 1
  }),
  [actions.RESET_PROVIDER_PAGE_NUM]: assign({
    numPagesToLoadInDrawer: () => 1
  }),
  [actions.SET_HAS_MORE_PROVIDERS_TO_LOAD]: assign({
    hasMoreProvidersToLoad: ({ context, event }) =>
      context.providersForDrawer._result.length <
      event.output._metadata.total_providers
  }),
  [actions.CLEAR_SEARCH_FILTERS]: assign({
    // TODO remove the filters from the searchParams
    searchParams: ({ event }) => event.data
  }),
  [actions.SELECT_SORT_OPTION]: assign({
    // TODO update the search params with the selected sort option
    searchParams: ({ event }) => event.data
  }),
  [actions.UPDATE_SEARCH_PARAMS]: assign({
    searchParams: ({ context, event }) => {
      return {
        ...context.searchParams,
        ...event.data
      };
    }
  }),
  [actions.SET_SNACKBAR_EXPIRATION]: () => {
    // Set an expiration for showing the snackbar again in local storage
    try {
      window.localStorage.setItem(
        MAP_SNACKBAR_EXPIRY_KEY,
        Date.now() + MAP_SNACKBAR_TTL
      );
    } catch (e) {
      /* Do Nothing */
    }
  }
};

const guards = {
  hasProvidersAndLocationsInContext: ({ context }) =>
    context.searchParams && context.providers && context.locations,

  hasSearchParamsAndProvidersInContext: ({ context }) =>
    context.searchParams && context.providers,

  hasSearchParamsInContext: ({ context }) => context.searchParams,

  // if we only have a single provider with type location, then we're in the
  // bookable location use case (urgent care)
  isSingleBookableLocation: ({ event }) =>
    event.output &&
    event.output._result &&
    event.output._result.length === 1 &&
    event.output._result[0].provider.entity_type === 'location',

  hasProviderResults: ({ event }) =>
    event.output && event.output._result && event.output._result.length > 0,

  hasProviderNoMlocData: ({ event }) => {
    const getPrimaryLocationFacet = (
      event.output?.providers?.facets ?? []
    ).find((loc) => loc.field === 'locations.primary_marketable_location_id');
    return (
      event.output &&
      getPrimaryLocationFacet?.terms &&
      !getPrimaryLocationFacet.terms.length
    );
  },

  hasLocationResults: ({ event }) =>
    event.output &&
    event.output._locations &&
    event.output._locations.length > 0,

  hasProviderAndLocationResults: ({ event }) => {
    const getPrimaryLocationFacet = (
      event.output?.providers?.facets ?? []
    ).find((loc) => loc.field === 'locations.primary_marketable_location_id');
    return (
      event.output &&
      getPrimaryLocationFacet?.terms &&
      getPrimaryLocationFacet.terms.length &&
      event.output.providers?._result &&
      event.output.providers?._result.length > 0 &&
      event.output.locations?._locations
    );
  },

  // check if we have deep link params in context
  hasValidDeepLinkingParams: ({ context }) => {
    const locationId =
      context.deepLinkingParams &&
      context.deepLinkingParams.locationId &&
      /^loc\d+$/i.test(context.deepLinkingParams.locationId) &&
      context.deepLinkingParams.locationId.toUpperCase();
    if (locationId) {
      // check if location exists in fetched api data
      return ((context.locations || {})._locations || []).some(
        (loc) => loc.id === locationId
      );
    }
    return false;
  },

  shouldShowSnackbar: () => {
    const now = new Date();
    let snackbarExpiry;

    try {
      snackbarExpiry = window.localStorage.getItem(MAP_SNACKBAR_EXPIRY_KEY);
    } catch (e) {
      /* Do Nothing */
    }

    if (!snackbarExpiry || now.getTime() > snackbarExpiry) {
      return true;
    }
  }
};

const services = {
  fetchProviders: async (context) => {
    const { fetchProviders, searchParams } = context;
    const providers = await fetchProviders(searchParams);
    return providers;
  },
  fetchLocations: async (context) => {
    const { fetchLocationsWithIds, providers } = context;
    const locationIds = getLocationIdsFromProvidersData(providers) || [];
    const locations = await fetchLocationsWithIds(locationIds);
    return locations;
  },
  fetchProvidersByLocationId: async (context) => {
    const {
      fetchProvidersByLocation,
      selectedLocation,
      searchParams,
      numPagesToLoadInDrawer
    } = context;

    const searchParamsForEvent = {
      ...searchParams,
      page: numPagesToLoadInDrawer
    };

    const providersForDrawer = await fetchProvidersByLocation(
      selectedLocation.id,
      searchParamsForEvent
    );
    return {
      ...providersForDrawer,
      _result: providersForDrawer._result.map((item) => {
        return {
          ...item,
          provider: {
            ...item.provider,
            has_provider_availability: !!item.availability?.slots?.length,
            has_provider_availability_new: !!item.availability?.slots?.filter(
              (s) => s.patient_relationship === PatientRelationship.NEW
            )?.length,
            has_provider_availability_established:
              !!item.availability?.slots?.filter(
                (s) =>
                  s.patient_relationship === PatientRelationship.ESTABLISHED
              )?.length
          }
        };
      })
    };
  },
  fetchSlotsForDrawer: async (context) => {
    const { fetchSlots, providersForDrawer, searchParams, config } = context;
    if (!config?.display_availability_in_search) return;
    const providerIds = (providersForDrawer?._result || []).map(
      ({ provider }) => provider.id
    );
    if (!providerIds.length) return;
    return fetchSlots(getApptInfoFromSearchParams(searchParams), providerIds);
  },
  fetchProvidersAndLocations: async (context) => {
    const { fetchProviders, fetchLocationsWithIds, searchParams } = context;
    const providers = await fetchProviders(searchParams);
    const locationIds = getLocationIdsFromProvidersData(providers) || [];
    const locations = await fetchLocationsWithIds(locationIds);

    // fetchProviders and fetchLocationsWithIds return null if they error
    if (providers === null || locations === null) {
      throw new Error('Error fetching providers and locations');
    }

    return {
      providers,
      locations
    };
  }
};

/**
 * Creates and returns the machine that orchestrates the map-search page
 * @param {object} initialParams the initial context of the machine
 * @returns xstate.Machine
 */
export const createMapSearchMachine = (initialParams) => {
  const initialContext = { ...emptyContext, ...initialParams };
  return createMachine(machineJSON(initialContext), {
    actions: machineActions,
    guards: guards,
    actors: fromServices(services)
  });
};
